import {Inject, Injectable, LOCALE_ID} from '@angular/core';
import {formatDate, FormStyle, getLocaleDayNames, getLocaleMonthNames, TranslationWidth} from '@angular/common';
import {DbDateStruct} from './date-struct';

export function NGB_DATEPICKER_18N_FACTORY(locale) {
  return new DbDatepickerI18nDefault(locale);
}

/**
 * A service supplying i18n data to the datepicker component.
 *
 * The default implementation of this service uses the Angular locale and registered locale data for
 * weekdays and month names (as explained in the Angular i18n guide).
 *
 * It also provides a way to i18n data that depends on calendar calculations, like aria labels, day, week and year
 * numerals. For other static labels the datepicker uses the default Angular i18n.
 *
 * See the [i18n demo](#/components/datepicker/examples#i18n) and
 * [Hebrew calendar demo](#/components/datepicker/calendars#hebrew) on how to extend this class and define
 * a custom provider for i18n.
 */
@Injectable({providedIn: 'root', useFactory: NGB_DATEPICKER_18N_FACTORY, deps: [LOCALE_ID]})
export abstract class DbDatepickerI18n {
  /** Returns the weekday label using specified width*/
  abstract getWeekdayLabel(weekday: number, width?: TranslationWidth): string;

  /**
   * Returns the full month name to display in the date picker navigation.
   *
   * With default calendar we use ISO 8601: 'month' is 1=Jan ... 12=Dec.
   */
  abstract getMonthFullName(month: number, year?: number): string;

  /** Returns the text label to display above the day view.*/
  getMonthLabel(date: DbDateStruct): string {
    return `${this.getMonthFullName(date.month, date.year)} ${this.getYearNumerals(date.year)}`;
  }

  /** Returns the value of the `aria-label` attribute for a specific date.*/
  abstract getDayAriaLabel(date: DbDateStruct): string;

  /** Returns the textual representation of a year that is rendered in the datepicker year select box.*/
  getYearNumerals(year: number): string { return `${year}`; }
}

/** A service providing default implementation for the datepicker i18n.
 * It can be used as a base implementation if necessary.*/
@Injectable()
export class DbDatepickerI18nDefault extends DbDatepickerI18n {
  private _monthsFull: string[];

  constructor(@Inject(LOCALE_ID) private _locale: string) {
    super();

    this._monthsFull = getLocaleMonthNames(_locale, FormStyle.Standalone, TranslationWidth.Wide) as string[];
  }

  getWeekdayLabel(weekday: number, width?: TranslationWidth): string {
    const weekdaysStartingOnSunday =
        getLocaleDayNames(this._locale, FormStyle.Standalone, width === undefined ? TranslationWidth.Short : width);
    const weekdays = weekdaysStartingOnSunday.map((day, index) => weekdaysStartingOnSunday[(index + 1) % 7]);
    return weekdays[weekday - 1] || '';
  }

  getMonthFullName(month: number): string { return this._monthsFull[month - 1] || ''; }

  getDayAriaLabel(date: DbDateStruct): string {
    const jsDate = new Date(date.year, date.month - 1, date.day);
    return formatDate(jsDate, 'fullDate', this._locale);
  }
}
